import React, { useState, useEffect, useRef, useCallback } from "react";
import { getApiBaseUrl } from "../../../../../helpers/apiUrl";
import MathContent from "../../../../customComponents/MathContent";
import ContentViewer from "../shared/ContentViewer";
import QuizPlayer from "../shared/QuizPlayer";
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Button,
  Grid,
  TextField,
  LinearProgress,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from "@mui/material";
import {
  ArrowBack,
  SportsEsports,
  CheckCircle,
  Cancel,
  AccessTime,
} from "@mui/icons-material";
import { keyframes } from "@emotion/react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import UserAvatar from "../../../../common/UserAvatar";
import {
  useJoinDuelQueueMutation,
  useLeaveDuelQueueMutation,
  useProposeDuelSettingsMutation,
  useAcceptDuelMutation,
  useRejectDuelMutation,
  useGetDuelQuery,
} from "../../../../../app/duelApiSlice";
import { useGetQuizResultQuery, useGetMyStudentSubjectsQuery } from "../../../../../app/quizApiSlice";
import { useGetLearnSessionQuery } from "../../../../../app/learnApiSlice";

// ─── Arena keyframes ───────────────────────────────────────────────────────────────
const vsPop = keyframes`
  0%   { transform: scale(0) rotate(-15deg); opacity: 0; }
  55%  { transform: scale(1.35) rotate(5deg); opacity: 1; }
  75%  { transform: scale(0.9) rotate(-2deg); opacity: 1; }
  100% { transform: scale(1) rotate(0deg);   opacity: 1; }
`;
const pulseRing = keyframes`
  0%   { transform: scale(1);    opacity: 0.7; }
  100% { transform: scale(2.2);  opacity: 0; }
`;
const arenaFloat = keyframes`
  0%, 100% { transform: translateY(0); }
  50%       { transform: translateY(-10px); }
`;
const arenaConfetti = keyframes`
  0%   { transform: translateY(-10px) rotate(0deg);   opacity: 1; }
  100% { transform: translateY(110px) rotate(540deg); opacity: 0; }
`;
const resultPop = keyframes`
  0%   { transform: scale(0.6); opacity: 0; }
  65%  { transform: scale(1.1); opacity: 1; }
  100% { transform: scale(1);   opacity: 1; }
`;

// Arena HUD header helper
const ArenaHUD = ({ onBack, label, sublabel }) => (
  <Box
    sx={{
      background: "linear-gradient(90deg, #7B0000 0%, #E02424 60%, #FF6B6B 100%)",
      borderRadius: "16px",
      px: 2.5,
      py: 2,
      mb: 3,
      display: "flex",
      alignItems: "center",
      gap: 1.5,
    }}
  >
    <IconButton
      onClick={onBack}
      size="small"
      sx={{ color: "rgba(255,255,255,0.8)", bgcolor: "rgba(255,255,255,0.12)", borderRadius: "10px" }}
    >
      <ArrowBack fontSize="small" />
    </IconButton>
    <Box>
      <Typography sx={{ fontWeight: 900, fontSize: "0.62rem", color: "rgba(255,255,255,0.55)", letterSpacing: 3, textTransform: "uppercase" }}>{sublabel}</Typography>
      <Typography sx={{ fontWeight: 800, fontSize: "1.1rem", color: "#fff" }}>{label}</Typography>
    </Box>
    <Box sx={{ ml: "auto" }}>
      <SportsEsports sx={{ fontSize: "1.8rem", color: "rgba(255,255,255,0.4)", animation: `${arenaFloat} 2.5s ease-in-out infinite` }} />
    </Box>
  </Box>
);

// Arena background wrapper
const ArenaBg = ({ children, sx = {} }) => (
  <Box
    sx={{
      background: "linear-gradient(160deg, #1C0000 0%, #3B0000 40%, #1C0000 75%, #0D0000 100%)",
      borderRadius: 0,
      p: { xs: 2.5, sm: 3 },
      pt: { xs: 2, sm: 2 },
      minHeight: "100vh",
      position: "relative",
      overflow: "hidden",
      ...sx,
    }}
  >
    {/* Dot grid */}
    <Box
      sx={{
        position: "absolute", inset: 0,
        backgroundImage: "radial-gradient(rgba(255,100,100,0.07) 1px, transparent 1px)",
        backgroundSize: "26px 26px",
        pointerEvents: "none",
      }}
    />
    <Box sx={{ position: "relative", zIndex: 1 }}>{children}</Box>
  </Box>
);

// ─── Step IDs ────────────────────────────────────────────────────────────────
const STEP_LOBBY = "lobby";
const STEP_WAITING = "waiting";
const STEP_MATCHED = "matched";
const STEP_PROPOSE = "propose";
const STEP_WAIT_ACCEPT = "wait_accept";
const STEP_LEARN = "learn";
const STEP_QUIZ = "quiz";
const STEP_RESULT = "result";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getInitials = (name = "") =>
  name.split(" ").slice(0, 2).map((w) => w[0]?.toUpperCase() || "").join("");

// ─── Duel Learn Step ───────────────────────────────────────────────────────────
const DuelLearnStep = ({ sessionId, onReady }) => {
  const { data, isLoading } = useGetLearnSessionQuery(sessionId, { skip: !sessionId });
  const session = data?.session;

  if (isLoading || !session) {
    return (
      <ArenaBg>
        <Box sx={{ textAlign: "center", py: 6 }}>
          <CircularProgress size={40} sx={{ color: "#E02424", mb: 2 }} />
          <Typography sx={{ color: "rgba(255,255,255,0.6)" }}>Loading your lesson…</Typography>
        </Box>
      </ArenaBg>
    );
  }

  return (
    <ContentViewer
      session={session}
      sessionId={sessionId}
      onContinue={onReady}
      continueLabel="I'm Ready — Fight! ⚔️"
      continueIcon={<SportsEsports />}
      accentColor="#C62828"
      headerGradient="linear-gradient(90deg, #7B0000 0%, #B71C1C 60%, #EF5350 100%)"
      modeLabel="Study First"
      ctaHeadline="Lesson complete — ready to duel?"
      ctaSubtext="Answer quiz questions as fast as possible!"
    />
  );
};

// ─── Subject Palettes (matches Practice mode) ────────────────────────────────
const SUBJECT_PALETTES = [
  { bg: "linear-gradient(145deg,#0d1b4b,#1a3080)", accent: "#4f8ef7", glow: "rgba(79,142,247,0.25)" },
  { bg: "linear-gradient(145deg,#1a0533,#3d0d7a)", accent: "#b06af3", glow: "rgba(176,106,243,0.25)" },
  { bg: "linear-gradient(145deg,#0a2e1a,#0f4d2a)", accent: "#34d399", glow: "rgba(52,211,153,0.25)" },
  { bg: "linear-gradient(145deg,#2d1500,#5a3000)", accent: "#fb923c", glow: "rgba(251,146,60,0.25)" },
  { bg: "linear-gradient(145deg,#1a1230,#2e1f5e)", accent: "#818cf8", glow: "rgba(129,140,248,0.25)" },
  { bg: "linear-gradient(145deg,#0f2030,#163550)", accent: "#38bdf8", glow: "rgba(56,189,248,0.25)" },
  { bg: "linear-gradient(145deg,#2d0a10,#5a141e)", accent: "#f87171", glow: "rgba(248,113,113,0.25)" },
  { bg: "linear-gradient(145deg,#0f2a1a,#1a4a2e)", accent: "#4ade80", glow: "rgba(74,222,128,0.25)" },
];

// ─── Subject Picker ────────────────────────────────────────────────────────────
const SubjectPicker = ({ value, onChange }) => {
  const { data: subjectsData } = useGetMyStudentSubjectsQuery();
  const subjects = subjectsData?.subjects || [];
  const [custom, setCustom] = useState(false);

  // When subjects load, default to first subject if nothing selected yet
  React.useEffect(() => {
    if (!value && subjects.length > 0 && !custom) {
      onChange(subjects[0].name);
    }
  }, [subjects.length]); // eslint-disable-line react-hooks/exhaustive-deps

  if (custom) {
    return (
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Button
            size="small" onClick={() => { setCustom(false); onChange(""); }}
            sx={{ color: "rgba(255,150,150,0.8)", minWidth: 0, p: 0.3, fontSize: "0.75rem" }}
          >
            ← Back to list
          </Button>
        </Box>
        <TextField
          fullWidth
          autoFocus
          label="Subject"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="e.g. Mathematics, Biology…"
          InputLabelProps={{ sx: { color: "rgba(255,255,255,0.55)" } }}
          sx={{
            "& .MuiOutlinedInput-root": {
              color: "#fff", bgcolor: "rgba(255,255,255,0.06)", borderRadius: "10px",
              "& fieldset": { borderColor: "rgba(255,100,100,0.3)" },
              "&:hover fieldset": { borderColor: "rgba(255,100,100,0.6)" },
              "&.Mui-focused fieldset": { borderColor: "#E02424" },
            },
          }}
        />
      </Box>
    );
  }

  return (
    <Box>
      <Typography sx={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.45)", mb: 1.2, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase" }}>
        Select Subject
      </Typography>
      <Grid container spacing={1.2}>
        {subjects.map((s, i) => {
          const pal = SUBJECT_PALETTES[i % SUBJECT_PALETTES.length];
          const selected = value === s.name;
          return (
            <Grid item xs={6} sm={4} key={s.id}>
              <Box
                onClick={() => onChange(s.name)}
                sx={{
                  p: 1.5, borderRadius: "12px", cursor: "pointer",
                  background: pal.bg,
                  border: selected ? `2px solid ${pal.accent}` : "1.5px solid rgba(255,255,255,0.08)",
                  boxShadow: selected ? `0 0 14px ${pal.glow}` : "none",
                  transition: "all 0.15s ease",
                  "&:hover": { border: `1.5px solid ${pal.accent}`, transform: "translateY(-1px)" },
                }}
              >
                <Typography
                  sx={{
                    fontWeight: selected ? 800 : 600,
                    fontSize: "0.82rem",
                    color: selected ? pal.accent : "rgba(255,255,255,0.75)",
                    textAlign: "center",
                    lineHeight: 1.3,
                  }}
                >
                  {s.name}
                </Typography>
              </Box>
            </Grid>
          );
        })}
        <Grid item xs={6} sm={4}>
          <Box
            onClick={() => { setCustom(true); onChange(""); }}
            sx={{
              p: 1.5, borderRadius: "12px", cursor: "pointer",
              background: "rgba(255,255,255,0.04)",
              border: "1.5px dashed rgba(255,255,255,0.18)",
              transition: "all 0.15s ease",
              "&:hover": { border: "1.5px dashed rgba(255,255,255,0.38)", bgcolor: "rgba(255,255,255,0.07)" },
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: "0.82rem", color: "rgba(255,255,255,0.38)", textAlign: "center" }}>
              Other…
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

// ─── Result Step ──────────────────────────────────────────────────────────────
const ResultStep = ({ duelId, myQuizId, onPlayAgain }) => {
  const authData = useSelector((state) => state.auth);
  const myUserId = Number(authData?.user?.id);

  const [bothDone, setBothDone] = React.useState(false);
  const { data: duelData } = useGetDuelQuery(duelId, {
    pollingInterval: bothDone ? 0 : 3000,
  });
  const duel = duelData?.duel || duelData;

  const { data: myResult, isLoading } = useGetQuizResultQuery({ quiz_id: myQuizId });

  const myStats = React.useMemo(() => {
    if (!myResult?.answers) return { correct: 0, total: 0, pct: 0 };
    let correct = 0;
    myResult.answers.forEach((a) => {
      if (a.question?.options?.find((o) => o.id === a.option_id && o.is_answer)) correct++;
    });
    const total = myResult.answers.length;
    return { correct, total, pct: total ? Math.round((correct / total) * 100) : 0 };
  }, [myResult]);

  const iAmPlayerOne = Number(duel?.player_one_id) === myUserId;
  const myPoints  = iAmPlayerOne ? duel?.player_one_points : duel?.player_two_points;
  const oppPoints = iAmPlayerOne ? duel?.player_two_points : duel?.player_one_points;
  const oppPlayer = iAmPlayerOne ? duel?.player_two : duel?.player_one;
  const mePlayer  = iAmPlayerOne ? duel?.player_one : duel?.player_two;

  const getDisplayName = (user) => {
    const p = user?.auth_profile;
    if (p?.first_name || p?.last_name) return `${p.first_name || ""} ${p.last_name || ""}`.trim();
    return user?.username || "—";
  };

  const myName  = getDisplayName(mePlayer) || "You";
  const oppName = getDisplayName(oppPlayer) || "Opponent";

  const opponentDone = oppPoints !== null && oppPoints !== undefined;
  const myDone       = myPoints  !== null && myPoints  !== undefined;

  React.useEffect(() => {
    if (opponentDone && myDone) setBothDone(true);
  }, [opponentDone, myDone]);

  const bothFinished = opponentDone && myDone;
  let resultLabel = null;
  let iWon = false;
  if (bothFinished) {
    if (myPoints > oppPoints)      { resultLabel = "🏆 You Won!"; iWon = true; }
    else if (myPoints < oppPoints) { resultLabel = "😔 You Lost"; }
    else                           { resultLabel = "🤝 It's a Draw!"; }
  }

  if (isLoading) return (
    <ArenaBg>
      <Box sx={{ textAlign: "center", py: 6 }}>
        <CircularProgress sx={{ color: "#E02424" }} />
      </Box>
    </ArenaBg>
  );

  const PlayerCard = ({ name, points, correct, total, pct, isMe, isWinner }) => {
    const winBorder = isWinner ? "2.5px solid #FBBF24" : bothFinished && !isWinner && myPoints !== oppPoints ? "2px solid rgba(220,38,38,0.5)" : "1.5px solid rgba(255,255,255,0.12)";
    const winBg = isWinner ? "rgba(251,191,36,0.08)" : bothFinished && !isWinner && myPoints !== oppPoints ? "rgba(220,38,38,0.12)" : "rgba(255,255,255,0.04)";
    const winShadow = isWinner ? "0 0 24px rgba(251,191,36,0.35), 0 4px 20px rgba(0,0,0,0.5)" : "none";
    return (
      <Box sx={{ p: 2.5, borderRadius: "16px", border: winBorder, bgcolor: winBg, boxShadow: winShadow, position: "relative", textAlign: "center", transition: "all 0.4s ease" }}>
        {bothFinished && (
          <Box
            sx={{
              position: "absolute", top: -10, left: "50%", transform: "translateX(-50%)",
              bgcolor: isWinner ? "#B45309" : myPoints === oppPoints ? "#D97706" : "#7B0000",
              color: isWinner ? "#FEF3C7" : "#fff", fontWeight: 700, fontSize: "0.6rem", px: 1.5, py: 0.3,
              borderRadius: "20px", letterSpacing: 1, textTransform: "uppercase",
              boxShadow: isWinner ? "0 2px 8px rgba(251,191,36,0.5)" : "none",
            }}
          >
            {isWinner ? "🏆 WINNER" : myPoints === oppPoints ? "DRAW" : "LOSER"}
          </Box>
        )}
        <UserAvatar
          src={isMe ? mePlayer?.auth_profile?.photo : oppPlayer?.auth_profile?.photo}
          name={name}
          sx={{
            width: 60, height: 60, mx: "auto", mb: 1.5, fontSize: "1.4rem",
            border: isWinner ? "2.5px solid #FBBF24" : "2px solid rgba(255,255,255,0.2)",
            boxShadow: isWinner ? "0 0 16px rgba(251,191,36,0.5)" : "none",
          }}
        />
        <Typography sx={{ fontWeight: 700, fontSize: "0.88rem", color: isWinner ? "#FDE68A" : "rgba(255,255,255,0.85)", mb: 1 }}>{name}</Typography>
        <Typography sx={{ fontWeight: 900, fontSize: "2.4rem", color: isWinner ? "#FBBF24" : isMe ? "#FF6B6B" : "#93c5fd", lineHeight: 1, textShadow: isWinner ? "0 0 16px rgba(251,191,36,0.5)" : "none" }}>
          {points ?? "—"}
        </Typography>
        <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.4)", mb: 0.5 }}>pts</Typography>
        {pct !== undefined && (
          <Typography sx={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.55)" }}>{pct}% · {correct}/{total} correct</Typography>
        )}
        {(points === null || points === undefined) && (
          <Box sx={{ mt: 1 }}>
            <CircularProgress size={18} sx={{ color: "rgba(255,255,255,0.4)" }} />
            <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.4)", mt: 0.5 }}>Still playing…</Typography>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <ArenaBg>
      {/* Confetti dots for win */}
      {iWon && Array.from({ length: 12 }).map((_, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            top: `${5 + (i * 7) % 25}%`,
            left: `${(i * 8.5) % 100}%`,
            width: 8, height: 8, borderRadius: "50%",
            bgcolor: ["#E02424", "#1565C0", "#D97706", "#15803D", "#fff"][i % 5],
            animation: `${arenaConfetti} ${1.2 + (i % 4) * 0.3}s ${i * 0.1}s ease-in both`,
          }}
        />
      ))}

      <Box sx={{ position: "relative", zIndex: 1 }}>
        {/* Title */}
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Typography sx={{ fontWeight: 900, fontSize: "1.6rem", color: "#fff", letterSpacing: 1 }}>DUEL RESULTS</Typography>
          {resultLabel && (
            <Typography
              sx={{
                fontWeight: 800, fontSize: "1.2rem", mt: 0.5,
                color: iWon ? "#86efac" : myPoints === oppPoints ? "#fde68a" : "#fca5a5",
                animation: `${resultPop} 0.6s cubic-bezier(0.22,1,0.36,1) both`,
              }}
            >
              {resultLabel}
            </Typography>
          )}
          {!bothFinished && (
            <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.85rem", mt: 1 }}>Waiting for opponent to finish…</Typography>
          )}
        </Box>

        {/* VS cards */}
        <Grid container spacing={2} justifyContent="center" sx={{ mb: 3 }}>
          <Grid item xs={5}>
            <PlayerCard
              name={myName}
              points={myPoints}
              correct={myStats.correct}
              total={myStats.total}
              pct={myStats.pct}
              isMe
              isWinner={bothFinished && myPoints > oppPoints}
            />
          </Grid>
          <Grid item xs={2} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography
              sx={{
                fontWeight: 900, fontSize: "1.8rem", color: "#FF4444",
                textShadow: "0 0 16px rgba(255,68,68,0.6)",
              }}
            >
              VS
            </Typography>
          </Grid>
          <Grid item xs={5}>
            <PlayerCard
              name={oppName}
              points={oppPoints}
              isWinner={bothFinished && oppPoints > myPoints}
            />
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
          <Button
            variant="contained"
            onClick={onPlayAgain}
            sx={{
              background: "linear-gradient(90deg, #7B0000 0%, #E02424 100%)",
              fontWeight: 800, borderRadius: "12px", px: 4, py: 1.3, letterSpacing: 0.5,
              boxShadow: "0 4px 16px rgba(224,36,36,0.4)",
              "&:hover": { background: "linear-gradient(90deg, #5B0000 0%, #C02020 100%)" },
            }}
          >
            Play Again
          </Button>
        </Box>
      </Box>
    </ArenaBg>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const DuelFlow = () => {
  const [step, setStep] = useState(STEP_LOBBY);
  const [error, setError] = useState("");
  const [duel, setDuel] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [completedQuizId, setCompletedQuizId] = useState(null);
  const [isProposer, setIsProposer] = useState(false);
  const [proposeForm, setProposeForm] = useState({ subject: "", topics: "", num_questions: 10, mode: "quiz" });
  const [learnSessionId, setLearnSessionId] = useState(null);

  const authData = useSelector((state) => state.auth);
  const myUserId = Number(authData?.user?.id);
  const navigate = useNavigate();
  const baseUrl = getApiBaseUrl();
  const accessToken = authData?.access_token;
  const queuePollRef = useRef(null);
  const duelPollRef = useRef(null);
  // Mirrors `step` so async poll callbacks can check the current step without
  // stale closure values — prevents a concurrent queue-poll from firing
  // 'expired' after a match has already been found.
  const stepRef = useRef(step);
  useEffect(() => { stepRef.current = step; }, [step]);

  const [joinQueue, { isLoading: isJoining }] = useJoinDuelQueueMutation();
  const [leaveQueue] = useLeaveDuelQueueMutation();
  const [proposeSettings, { isLoading: isProposing }] = useProposeDuelSettingsMutation();
  const [acceptDuel, { isLoading: isAccepting }] = useAcceptDuelMutation();
  const [rejectDuel] = useRejectDuelMutation();

  // Poll duel status via SSE when we have a duel ID
  const startDuelStatusPoll = useCallback((duelId) => {
    if (duelPollRef.current) clearInterval(duelPollRef.current);

    const poll = async () => {
      try {
        const res = await fetch(`${baseUrl}/duel/${duelId}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        const d = data.duel || data;
        setDuel(d);

        if (d.status === "active" && d.quiz?.id) {
          // DuelSchema nests the quiz. Transition as soon as the quiz object
          // exists — QuizStep handles the empty-questions loading state.
          const quizData = d.quiz;
          if (quizData?.id) {
            setQuiz(quizData);
            clearInterval(duelPollRef.current);
            // For learn mode (proposer path): show lesson before quiz
            if (d.mode === "learn") {
              const sessionId = d.player_one_id === myUserId
                ? d.learn_session_one_id
                : d.learn_session_two_id;
              if (sessionId) {
                setLearnSessionId(sessionId);
                setStep(STEP_LEARN);
              } else {
                setStep(STEP_QUIZ);
              }
            } else {
              setStep(STEP_QUIZ);
            }
          }
          // else: duel is active but quiz not yet visible in this poll — retry
        } else if (d.status === "settings_proposed") {
          // Unblock players stuck in STEP_WAIT_ACCEPT due to race conditions:
          // if it turns out the OTHER player is the proposer, switch to acceptor role.
          if (Number(d.proposed_by_id) !== myUserId) {
            setIsProposer(false);
            setStep(STEP_MATCHED);
          }
          // If we are still the proposer, stay in STEP_WAIT_ACCEPT — no change needed.
        }
      } catch (e) { /* silent */ }
    };

    poll();
    duelPollRef.current = setInterval(poll, 4000);
  }, [baseUrl, accessToken, myUserId]);

  // Poll queue status via SSE
  const startQueueStatusPoll = useCallback((queueId) => {
    if (queuePollRef.current) clearInterval(queuePollRef.current);

    const poll = async () => {
      try {
        const res = await fetch(`${baseUrl}/duel/queue/status`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!res.ok) return;
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let lineBuffer = "";

        const timeout = setTimeout(() => reader.cancel(), 15000);

        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            lineBuffer += decoder.decode(value, { stream: true });
            const lines = lineBuffer.split("\n");
            lineBuffer = lines[lines.length - 1];
            for (let i = 0; i < lines.length - 1; i++) {
              const line = lines[i].trim();
              if (!line.startsWith("data: ")) continue;
              let parsed;
              try { parsed = JSON.parse(line.substring(6).trim()); } catch { continue; }
              if (parsed.type === "matched" && parsed.duel?.id) {
                // Guard: another concurrent poll may have already handled this
                if (stepRef.current !== STEP_WAITING) return;
                clearInterval(queuePollRef.current);
                clearTimeout(timeout);
                setDuel(parsed.duel);
                setStep(STEP_MATCHED);
                startDuelStatusPoll(parsed.duel.id);
                return;
              } else if (parsed.type === "expired") {
                // Guard: if we already moved to MATCHED (or beyond), ignore this
                // stale event from a concurrent poll invocation.
                if (stepRef.current !== STEP_WAITING) return;
                clearInterval(queuePollRef.current);
                clearTimeout(timeout);
                setStep(STEP_LOBBY);
                setError("Match search expired. Try again.");
                return;
              }
            }
          }
        } finally {
          clearTimeout(timeout);
        }
      } catch (e) { /* silent — will retry */ }
    };

    poll();
    queuePollRef.current = setInterval(poll, 5000);
  }, [baseUrl, accessToken, startDuelStatusPoll]);

  useEffect(() => {
    return () => {
      if (queuePollRef.current) clearInterval(queuePollRef.current);
      if (duelPollRef.current) clearInterval(duelPollRef.current);
    };
  }, []);

  const handleFindMatch = async () => {
    setError("");
    try {
      const result = await joinQueue().unwrap();
      if (result.matched && result.duel) {
        setDuel(result.duel);
        setStep(STEP_MATCHED);
        startDuelStatusPoll(result.duel.id);
      } else {
        setStep(STEP_WAITING);
        startQueueStatusPoll(result.queue_id);
      }
    } catch (err) {
      setError(err?.data?.message || "Failed to join queue. Please try again.");
    }
  };

  const handleLeaveQueue = async () => {
    if (queuePollRef.current) clearInterval(queuePollRef.current);
    await leaveQueue().catch(() => {});
    setStep(STEP_LOBBY);
  };

  const handlePropose = async () => {
    if (!duel?.id) return;
    setError("");
    try {
      const topics = proposeForm.topics.split(",").map((t) => t.trim()).filter(Boolean);
      const result = await proposeSettings({ id: duel.id, subject: proposeForm.subject, topics, num_questions: proposeForm.num_questions, mode: proposeForm.mode }).unwrap();
      if (result.already_proposed) {
        // Opponent already proposed while we were filling the form — become the acceptor
        setDuel(result.duel);
        setIsProposer(false);
        setStep(STEP_MATCHED);
        startDuelStatusPoll(result.duel.id);
      } else {
        if (result.duel) setDuel(result.duel);
        setIsProposer(true);
        setStep(STEP_WAIT_ACCEPT);
        startDuelStatusPoll(duel.id);
      }
    } catch (err) {
      setError(err?.data?.message || "Failed to propose settings.");
    }
  };

  const handleAccept = async () => {
    if (!duel?.id) return;
    setError("");
    try {
      const result = await acceptDuel(duel.id).unwrap();
      if (result.quiz?.id) {
        setQuiz(result.quiz);
        if (result.learn_session_id) {
          // Learn mode: show lesson before the quiz
          setLearnSessionId(result.learn_session_id);
          setStep(STEP_LEARN);
        } else {
          setStep(STEP_QUIZ);
        }
      } else {
        // Fallback: quiz not yet in the response — wait for the duel poll
        setStep(STEP_WAIT_ACCEPT);
        startDuelStatusPoll(duel.id);
      }
    } catch (err) {
      setError(err?.data?.message || "Failed to accept duel.");
    }
  };

  const handleReject = async () => {
    if (!duel?.id) return;
    await rejectDuel(duel.id).catch(() => {});
    setStep(STEP_MATCHED);
  };

  const handlePlayAgain = () => {
    if (duelPollRef.current) clearInterval(duelPollRef.current);
    setStep(STEP_LOBBY);
    setDuel(null);
    setQuiz(null);
    setCompletedQuizId(null);
    setIsProposer(false);
    setLearnSessionId(null);
    setError("");
  };

  // ── LOBBY ────────────────────────────────────────────────────────────────
  if (step === STEP_LOBBY) {
    return (
      <ArenaBg>
        <ArenaHUD onBack={() => navigate("/lms/student/quiz/play")} label="DUEL MODE" sublabel="Arena" />

        <Box sx={{ textAlign: "center", py: 3 }}>
          {/* Pulsing icon */}
          <Box sx={{ position: "relative", width: 100, height: 100, mx: "auto", mb: 3 }}>
            <Box
              sx={{
                position: "absolute", inset: 0, borderRadius: "50%",
                border: "3px solid rgba(224,36,36,0.5)",
                animation: `${pulseRing} 1.6s ease-out infinite`,
              }}
            />
            <Box
              sx={{
                position: "absolute", inset: 0, borderRadius: "50%",
                border: "3px solid rgba(224,36,36,0.3)",
                animation: `${pulseRing} 1.6s 0.4s ease-out infinite`,
              }}
            />
            <Box
              sx={{
                width: 100, height: 100, borderRadius: "50%",
                background: "linear-gradient(135deg, #7B0000 0%, #E02424 100%)",
                display: "flex", alignItems: "center", justifyContent: "center",
                border: "3px solid rgba(255,255,255,0.15)",
              }}
            >
              <SportsEsports sx={{ fontSize: "2.8rem", color: "#fff" }} />
            </Box>
          </Box>

          <Typography sx={{ fontWeight: 900, fontSize: "1.6rem", color: "#fff", mb: 0.5, letterSpacing: 1 }}>ENTER THE ARENA</Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.6)", mb: 4, maxWidth: 380, mx: "auto", fontSize: "0.9rem" }}>
            Join the queue and get instantly matched with a classmate. First to finish wins!
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2, bgcolor: "rgba(220,38,38,0.15)", color: "#fca5a5", border: "1px solid rgba(224,36,36,0.3)", "& .MuiAlert-icon": { color: "#fca5a5" } }}>{error}</Alert>}

          <Button
            variant="contained"
            size="large"
            onClick={handleFindMatch}
            disabled={isJoining}
            startIcon={isJoining ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <SportsEsports />}
            sx={{
              background: "linear-gradient(90deg, #7B0000 0%, #E02424 60%, #FF6B6B 100%)",
              px: 5, py: 1.8, fontSize: "1.05rem", fontWeight: 900,
              borderRadius: "14px", textTransform: "uppercase", letterSpacing: 1,
              boxShadow: "0 6px 24px rgba(224,36,36,0.5)",
              "&:hover": { background: "linear-gradient(90deg, #5B0000 0%, #C02020 100%)", boxShadow: "0 8px 28px rgba(224,36,36,0.65)" },
            }}
          >
            {isJoining ? "Joining…" : "Find a Match"}
          </Button>
        </Box>
      </ArenaBg>
    );
  }

  // ── WAITING ──────────────────────────────────────────────────────────────
  if (step === STEP_WAITING) {
    return (
      <ArenaBg>
        <Box sx={{ textAlign: "center", py: 5 }}>
          <Box sx={{ position: "relative", width: 80, height: 80, mx: "auto", mb: 3 }}>
            <Box sx={{ position: "absolute", inset: -10, borderRadius: "50%", border: "3px solid rgba(224,36,36,0.4)", animation: `${pulseRing} 1.4s ease-out infinite` }} />
            <Box sx={{ position: "absolute", inset: -10, borderRadius: "50%", border: "3px solid rgba(224,36,36,0.25)", animation: `${pulseRing} 1.4s 0.5s ease-out infinite` }} />
            <CircularProgress size={80} thickness={3} sx={{ color: "#E02424", position: "absolute", inset: 0 }} />
            <SportsEsports sx={{ fontSize: "2.2rem", color: "#fff", position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }} />
          </Box>
          <Typography sx={{ fontWeight: 900, fontSize: "1.5rem", color: "#fff", mb: 0.8, letterSpacing: 0.5 }}>FINDING OPPONENT…</Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.55)", mb: 4, fontSize: "0.88rem" }}>Scanning your school for a worthy challenger. Hold tight!</Typography>
          <Button
            variant="outlined"
            onClick={handleLeaveQueue}
            sx={{ color: "rgba(255,100,100,0.9)", borderColor: "rgba(224,36,36,0.5)", fontWeight: 700, borderRadius: "10px", "&:hover": { bgcolor: "rgba(224,36,36,0.1)" } }}
          >
            Cancel
          </Button>
        </Box>
      </ArenaBg>
    );
  }

  // ── MATCHED ──────────────────────────────────────────────────────────────
  if (step === STEP_MATCHED) {
    const otherUser = duel?.proposed_by_id !== authData?.user?.id ? duel?.proposed_by : null;
    const hasPendingProposal = duel?.status === "settings_proposed";
    const proposedByMe = hasPendingProposal && duel?.proposed_by_id === authData?.user?.id;

    return (
      <ArenaBg>
        <ArenaHUD onBack={handlePlayAgain} label="MATCH FOUND!" sublabel="Arena" />

        {/* VS display */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: { xs: 2, sm: 4 }, mb: 4 }}>
          <Box sx={{ textAlign: "center" }}>
            <UserAvatar
              src={authData?.user?.photo || undefined}
              name={authData?.user?.name || "Me"}
              sx={{
                width: 88, height: 88, mx: "auto", mb: 1.2, fontSize: "1.8rem",
                border: "3px solid rgba(255,255,255,0.2)",
                boxShadow: "0 0 24px rgba(224,36,36,0.55)",
              }}
            />
            <Typography sx={{ fontWeight: 800, color: "#fff", fontSize: "0.9rem" }}>
              {authData?.user?.name || "You"}
            </Typography>
            <Typography sx={{ fontWeight: 600, color: "rgba(255,255,255,0.45)", fontSize: "0.72rem" }}>You</Typography>
          </Box>

          <Typography
            sx={{
              fontWeight: 900, fontSize: { xs: "2.4rem", sm: "3rem" }, color: "#FF4444",
              textShadow: "0 0 20px rgba(255,68,68,0.7), 0 2px 4px rgba(0,0,0,0.5)",
              animation: `${vsPop} 0.6s cubic-bezier(0.22,1,0.36,1) both`,
              letterSpacing: 2,
            }}
          >
            VS
          </Typography>

          <Box sx={{ textAlign: "center" }}>
            <UserAvatar
              src={duel?.player_two?.auth_profile?.photo || duel?.player_one?.auth_profile?.photo || undefined}
              name={(() => {
                const opp = Number(duel?.player_one_id) === myUserId ? duel?.player_two : duel?.player_one;
                const p = opp?.auth_profile;
                if (p?.first_name || p?.last_name) return `${p.first_name || ""} ${p.last_name || ""}`.trim();
                return opp?.username || "?";
              })()}
              sx={{
                width: 88, height: 88, mx: "auto", mb: 1.2, fontSize: "1.8rem",
                border: "3px solid rgba(255,255,255,0.2)",
                boxShadow: "0 0 24px rgba(21,101,192,0.55)",
              }}
            />
            <Typography sx={{ fontWeight: 800, color: "#fff", fontSize: "0.9rem" }}>
              {(() => {
                const opp = Number(duel?.player_one_id) === myUserId ? duel?.player_two : duel?.player_one;
                const p = opp?.auth_profile;
                if (p?.first_name || p?.last_name) return `${p.first_name || ""} ${p.last_name || ""}`.trim();
                return opp?.username || "Opponent";
              })()}
            </Typography>
            <Typography sx={{ fontWeight: 600, color: "rgba(255,255,255,0.45)", fontSize: "0.72rem" }}>Opponent</Typography>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2, bgcolor: "rgba(220,38,38,0.15)", color: "#fca5a5", border: "1px solid rgba(224,36,36,0.3)", "& .MuiAlert-icon": { color: "#fca5a5" } }}>{error}</Alert>}

        {!hasPendingProposal && (
          <Box sx={{ textAlign: "center" }}>
            <Typography sx={{ color: "rgba(255,255,255,0.6)", mb: 3, fontSize: "0.9rem" }}>
              Propose the quiz settings, or wait for your opponent to propose.
            </Typography>
            <Button
              variant="contained"
              onClick={() => setStep(STEP_PROPOSE)}
              sx={{
                background: "linear-gradient(90deg, #7B0000 0%, #E02424 100%)",
                px: 4, py: 1.3, fontWeight: 800, borderRadius: "12px", letterSpacing: 0.5,
                boxShadow: "0 4px 16px rgba(224,36,36,0.45)",
                "&:hover": { background: "linear-gradient(90deg, #5B0000 0%, #C02020 100%)" },
              }}
            >
              Set Quiz Terms
            </Button>
          </Box>
        )}

        {hasPendingProposal && proposedByMe && (
          <Box sx={{ p: 2, bgcolor: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: "14px", display: "flex", alignItems: "center", gap: 1.5 }}>
            <AccessTime sx={{ color: "rgba(255,200,200,0.8)" }} />
            <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.9rem" }}>Waiting for your opponent to accept the quiz settings…</Typography>
          </Box>
        )}

        {hasPendingProposal && !proposedByMe && duel?.proposed_settings && (
          <Box sx={{ p: 2.5, bgcolor: "rgba(224,36,36,0.12)", border: "1.5px solid rgba(224,36,36,0.4)", borderRadius: "14px" }}>
            <Typography sx={{ fontWeight: 700, mb: 1, color: "#fff" }}>Opponent proposes:</Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)" }}><strong>Subject:</strong> {duel.proposed_settings.subject}</Typography>
            {duel.proposed_settings.topics?.length > 0 && (
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)" }}><strong>Topics:</strong> {duel.proposed_settings.topics.join(", ")}</Typography>
            )}
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)" }}><strong>Questions:</strong> {duel.proposed_settings.num_questions}</Typography>
            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <Button
                variant="contained" color="success" onClick={handleAccept} disabled={isAccepting}
                startIcon={isAccepting ? <CircularProgress size={16} /> : <CheckCircle />}
                sx={{ fontWeight: 700, borderRadius: "10px", boxShadow: "0 4px 12px rgba(22,163,74,0.4)" }}
              >
                Accept
              </Button>
              <Button
                variant="outlined" color="error" onClick={handleReject}
                startIcon={<Cancel />}
                sx={{ fontWeight: 700, borderRadius: "10px", borderColor: "rgba(255,100,100,0.6)", color: "rgba(255,100,100,0.9)" }}
              >
                Reject
              </Button>
            </Box>
          </Box>
        )}
      </ArenaBg>
    );
  }

  // ── PROPOSE SETTINGS ─────────────────────────────────────────────────────
  if (step === STEP_PROPOSE) {
    return (
      <ArenaBg>
        <ArenaHUD onBack={() => setStep(STEP_MATCHED)} label="SET QUIZ TERMS" sublabel="Arena" />

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <SubjectPicker
              value={proposeForm.subject}
              onChange={(v) => setProposeForm((f) => ({ ...f, subject: v }))}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth label="Topics (comma-separated)" value={proposeForm.topics}
              placeholder="e.g. algebra, fractions, equations"
              onChange={(e) => setProposeForm((f) => ({ ...f, topics: e.target.value }))}
              InputLabelProps={{ sx: { color: "rgba(255,255,255,0.55)" } }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  color: "#fff", bgcolor: "rgba(255,255,255,0.06)", borderRadius: "10px",
                  "& fieldset": { borderColor: "rgba(255,100,100,0.3)" },
                  "&:hover fieldset": { borderColor: "rgba(255,100,100,0.6)" },
                  "&.Mui-focused fieldset": { borderColor: "#E02424" },
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "rgba(255,255,255,0.45)", letterSpacing: 1.5, textTransform: "uppercase", mb: 1 }}>
              Questions
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              {[5, 10, 20].map((n) => (
                <Box
                  key={n}
                  onClick={() => setProposeForm((f) => ({ ...f, num_questions: n }))}
                  sx={{
                    px: 2.5, py: 1, borderRadius: "10px", cursor: "pointer", fontWeight: 800, fontSize: "1rem",
                    color: proposeForm.num_questions === n ? "#fff" : "rgba(255,255,255,0.5)",
                    background: proposeForm.num_questions === n ? "linear-gradient(90deg, #990000, #E02424)" : "rgba(255,255,255,0.06)",
                    border: proposeForm.num_questions === n ? "1.5px solid rgba(224,36,36,0.8)" : "1.5px solid rgba(255,255,255,0.1)",
                    transition: "all 0.15s ease",
                    "&:hover": { background: proposeForm.num_questions === n ? "linear-gradient(90deg, #990000, #E02424)" : "rgba(255,255,255,0.1)" },
                  }}
                >
                  {n}
                </Box>
              ))}
            </Box>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel sx={{ color: "rgba(255,255,255,0.55)" }}>Mode</InputLabel>
              <Select
                label="Mode" value={proposeForm.mode}
                onChange={(e) => setProposeForm((f) => ({ ...f, mode: e.target.value }))}
                MenuProps={{ style: { zIndex: 1500 } }}
                sx={{
                  color: "#fff", bgcolor: "rgba(255,255,255,0.06)", borderRadius: "10px",
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,100,100,0.3)" },
                  "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.5)" },
                }}
              >
                <MenuItem value="quiz">Quiz only</MenuItem>
                <MenuItem value="learn">Learn then Quiz</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {error && <Alert severity="error" sx={{ mt: 2, bgcolor: "rgba(220,38,38,0.15)", color: "#fca5a5", border: "1px solid rgba(224,36,36,0.3)", "& .MuiAlert-icon": { color: "#fca5a5" } }}>{error}</Alert>}

        <Button
          fullWidth variant="contained" onClick={handlePropose} disabled={isProposing || !proposeForm.subject}
          sx={{
            mt: 3,
            background: "linear-gradient(90deg, #7B0000 0%, #E02424 100%)",
            py: 1.6, fontWeight: 900, borderRadius: "12px", fontSize: "1rem", letterSpacing: 0.5,
            boxShadow: "0 4px 16px rgba(224,36,36,0.4)",
            "&:hover": { background: "linear-gradient(90deg, #5B0000 0%, #C02020 100%)" },
          }}
        >
          {isProposing ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Propose Settings"}
        </Button>
      </ArenaBg>
    );
  }

  // ── WAIT FOR ACCEPT ───────────────────────────────────────────────────────
  if (step === STEP_WAIT_ACCEPT) {
    return (
      <ArenaBg>
        <Box sx={{ textAlign: "center", py: 4 }}>
          <CircularProgress size={56} thickness={3} sx={{ color: "#E02424", mb: 2.5 }} />
          <Typography sx={{ fontWeight: 900, fontSize: "1.3rem", color: "#fff", mb: 1 }}>
            {isProposer ? "Waiting for opponent…" : "Preparing your quiz…"}
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.55)", mb: 3, fontSize: "0.88rem" }}>
            {isProposer ? "Your opponent is reviewing the quiz settings." : "The quiz is being generated. Hold tight!"}
          </Typography>
          <Button
            variant="outlined" onClick={handlePlayAgain}
            sx={{ color: "rgba(255,100,100,0.9)", borderColor: "rgba(224,36,36,0.5)", fontWeight: 700, borderRadius: "10px", "&:hover": { bgcolor: "rgba(224,36,36,0.1)" } }}
          >
            Cancel
          </Button>
        </Box>
      </ArenaBg>
    );
  }

  // ── LEARN ─────────────────────────────────────────────────────────────────
  if (step === STEP_LEARN && learnSessionId) {
    return (
      <DuelLearnStep
        sessionId={learnSessionId}
        onReady={() => setStep(STEP_QUIZ)}
      />
    );
  }

  // ── QUIZ ─────────────────────────────────────────────────────────────────
  if (step === STEP_QUIZ && quiz) {
    const myName = authData?.user?.name || "You";
    const oppUser = duel?.proposed_by_id !== authData?.user?.id
      ? duel?.proposed_by
      : (duel?.recipient || duel?.opponent);
    const oppName = oppUser?.name || "Opponent";
    return (
      <Box>
        {/* VS Race Bar */}
        <Box
          sx={{
            background: "linear-gradient(90deg, #7B0000 0%, #E02424 60%, #FF6B6B 100%)",
            borderRadius: "16px 16px 0 0",
            px: 2, py: 1.2, mb: 0,
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}
        >
          {/* My side */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <UserAvatar
              name={myName}
              sx={{ width: 30, height: 30, fontSize: "0.72rem", bgcolor: "rgba(255,255,255,0.25)", border: "2px solid rgba(255,255,255,0.5)" }}
            />
            <Typography sx={{ fontWeight: 800, fontSize: "0.82rem", color: "#fff" }}>{myName.split(" ")[0]}</Typography>
          </Box>
          {/* Centre VS */}
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Typography sx={{ fontWeight: 900, fontSize: "0.72rem", color: "#FBBF24", letterSpacing: 2, lineHeight: 1 }}>⚡ VS ⚡</Typography>
            <Typography sx={{ fontSize: "0.62rem", color: "rgba(255,255,255,0.5)", mt: 0.2 }}>{quiz.subject}</Typography>
          </Box>
          {/* Opponent side */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontWeight: 800, fontSize: "0.82rem", color: "#fff" }}>{oppName.split(" ")[0]}</Typography>
            <UserAvatar
              name={oppName}
              sx={{ width: 30, height: 30, fontSize: "0.72rem", bgcolor: "#1565C0", border: "2px solid rgba(255,255,255,0.3)" }}
            />
          </Box>
        </Box>
        <QuizPlayer quiz={quiz} onDone={(qid) => { setCompletedQuizId(qid); setStep(STEP_RESULT); }} accentColor="#E02424" mode="DUEL" />
      </Box>
    );
  }

  // ── RESULT ───────────────────────────────────────────────────────────────
  if (step === STEP_RESULT && completedQuizId && duel?.id) {
    return (
      <Box>
        <ResultStep duelId={duel.id} myQuizId={completedQuizId} onPlayAgain={handlePlayAgain} />
      </Box>
    );
  }

  return null;
};

export default DuelFlow;
